import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, IdCard } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewCard = ({ r, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (approve) => {
    if (!approve && !reason.trim() && !window.confirm("Reject without a reason?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/age-reviews/${r.id}/decide`, { approve, reason: reason.trim() });
      toast.success(approve ? `${r.user.display_name} is now 18+ verified` : "Rejected. The member has been told why.");
      onDone(r.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the decision"); setBusy(false); }
  };
  return (
    <div data-testid={`age-review-${r.id}`} className="rounded-2xl border border-gold/25 bg-surface overflow-hidden">
      <div className="grid grid-cols-2 gap-px bg-white/5">
        {[["id_url", "ID document"], ["selfie_url", "Selfie with ID"]].map(([k, l]) => (
          <a key={k} href={`${BASE}${r[k]}`} target="_blank" rel="noreferrer" className="relative block aspect-[4/5] bg-black">
            <img src={`${BASE}${r[k]}`} alt={l} className="w-full h-full object-cover" />
            <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-[0.15em] bg-black/60 px-2 py-1 rounded-full">{l}</span>
          </a>
        ))}
      </div>
      <div className="p-4 space-y-3">
        <div>
          <p className="text-sm">{r.user.display_name} <span className="text-text-muted">· says {r.user.age}</span>{r.user.is_creator && <span className="ml-2 text-[9px] uppercase tracking-[0.15em] text-gold">Creator</span>}</p>
          <p className="text-xs text-text-muted break-all">{r.user.email}</p>
        </div>
        <p className="text-[11px] text-text-muted">Check the date of birth shows 18+, the ID looks genuine, and the face matches the selfie.</p>
        <input data-testid={`age-review-reason-${r.id}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
          placeholder="Reason if rejecting (sent to member)" className="w-full bg-transparent border-b border-white/15 py-2 text-sm focus:border-gold outline-none" />
        <div className="flex gap-2">
          <button data-testid={`age-review-approve-${r.id}`} disabled={busy} onClick={() => decide(true)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-emerald-600/80 hover:bg-emerald-600 text-xs uppercase tracking-[0.15em] disabled:opacity-50"><Check size={14} /> Approve 18+</button>
          <button data-testid={`age-review-reject-${r.id}`} disabled={busy} onClick={() => decide(false)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full border border-red-400/40 text-red-300 text-xs uppercase tracking-[0.15em] disabled:opacity-50"><X size={14} /> Reject</button>
        </div>
      </div>
    </div>
  );
};

export const AgeReviews = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/age-reviews").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows?.length) return null;
  return (
    <section data-testid="age-reviews" className="mb-12">
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-2 mb-1"><IdCard size={13} /> 18+ ID reviews · {rows.length}</p>
      <p className="text-xs text-text-muted mb-5">Photos are deleted as soon as you approve or reject.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((r) => <ReviewCard key={r.id} r={r} onDone={(id) => setRows((xs) => xs.filter((x) => x.id !== id))} />)}
      </div>
    </section>
  );
};
